import { config } from '@/config'

export interface AdminLoginResponse {
  success: boolean
  message: string
}

export interface AdminValidateResponse {
  success: boolean
  email?: string
  authToken?: string
  message: string
}

export class AuthService {
  private static authToken: string | null = null
  private static adminEmail: string | null = null

  /**
   * Send magic link to admin email
   */
  static async sendMagicLink(email: string): Promise<AdminLoginResponse> {
    const response = await fetch(config.api.sendAdminAuth, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    return await response.json()
  }

  /**
   * Validate magic link token and authenticate admin
   */
  static async validateMagicLink(token: string): Promise<AdminValidateResponse & { debug?: any }> {
    // Base64Url tokens are already URL-safe, don't double-encode
    const requestUrl = `${config.api.validateAdminAuth}?token=${token}`
    
    console.log('[AuthService] Validating magic link:', {
      token: token.substring(0, 20) + '...',
      requestUrl,
      timestamp: new Date().toISOString()
    })
    
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const responseText = await response.text()
    let result: AdminValidateResponse
    
    console.log('[AuthService] Response received:', {
      status: response.status,
      statusText: response.statusText,
      responseText,
      headers: Object.fromEntries(response.headers.entries())
    })

    try {
      result = JSON.parse(responseText)
    } catch (parseError) {
      console.error('[AuthService] Failed to parse response as JSON:', parseError)
      throw new Error(`Invalid response format: ${responseText}`)
    }

    if (!response.ok) {
      const errorWithDebug = new Error(`HTTP ${response.status}: ${response.statusText}`) as any
      errorWithDebug.debug = {
        token,
        requestUrl,
        status: response.status,
        responseBody: responseText,
        timestamp: new Date().toISOString()
      }
      throw errorWithDebug
    }

    // Store auth token and email if validation successful
    if (result.success && result.authToken && result.email) {
      console.log('[AuthService] Received session token:', result.authToken.substring(0, 20) + '...')
      console.log('[AuthService] Session token length:', result.authToken.length)
      
      this.authToken = result.authToken
      this.adminEmail = result.email
      // Store in localStorage for persistence
      localStorage.setItem('adminAuthToken', result.authToken)
      localStorage.setItem('adminEmail', result.email)
      
      console.log('[AuthService] Authentication successful:', {
        email: result.email,
        hasToken: !!result.authToken,
        tokenPreview: result.authToken.substring(0, 20) + '...'
      })
    } else {
      console.warn('[AuthService] Authentication failed:', result)
    }

    return { ...result, debug: {
      token,
      requestUrl,
      status: response.status,
      responseBody: responseText,
      timestamp: new Date().toISOString()
    }}
  }

  /**
   * Sign out current admin
   */
  static signOut(): void {
    this.authToken = null
    this.adminEmail = null
    localStorage.removeItem('adminAuthToken')
    localStorage.removeItem('adminEmail')
  }

  /**
   * Get current auth token for API requests
   */
  static getAuthToken(): string | null {
    if (this.authToken) {
      return this.authToken
    }
    
    // Try to restore from localStorage
    const storedToken = localStorage.getItem('adminAuthToken')
    if (storedToken) {
      this.authToken = storedToken
      this.adminEmail = localStorage.getItem('adminEmail')
      return storedToken
    }
    
    return null
  }

  /**
   * Get current admin email
   */
  static getAdminEmail(): string | null {
    if (this.adminEmail) {
      return this.adminEmail
    }
    
    // Try to restore from localStorage
    const storedEmail = localStorage.getItem('adminEmail')
    if (storedEmail) {
      this.adminEmail = storedEmail
      return storedEmail
    }
    
    return null
  }

  /**
   * Check if admin is currently authenticated
   */
  static isAuthenticated(): boolean {
    return !!this.getAuthToken()
  }

  /**
   * Validate current token by making a test API call
   */
  static async validateCurrentToken(): Promise<boolean> {
    const token = this.getAuthToken()
    if (!token) {
      console.log('[AuthService] validateCurrentToken: No token available')
      return false
    }

    console.log('[AuthService] validateCurrentToken: Using token:', token.substring(0, 20) + '...')
    console.log('[AuthService] validateCurrentToken: Token length:', token.length)
    console.log('[AuthService] validateCurrentToken: Making request to:', config.api.getInvitations)

    try {
      // Make a simple API call to test if token is still valid
      const response = await fetch(config.api.getInvitations, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      console.log('[AuthService] validateCurrentToken: Response status:', response.status)
      console.log('[AuthService] validateCurrentToken: Response ok:', response.ok)

      if (response.status === 401) {
        // Token is expired or invalid - clear auth state
        console.log('[AuthService] Token validation failed - clearing auth state')
        const responseText = await response.text()
        console.log('[AuthService] 401 Response body:', responseText)
        this.signOut()
        return false
      }

      return response.ok
    } catch (error) {
      console.error('[AuthService] Token validation error:', error)
      return false
    }
  }

  /**
   * Get authorization header for API requests
   */
  static getAuthHeader(): string | null {
    const token = this.getAuthToken()
    return token ? `Bearer ${token}` : null
  }

  /**
   * Initialize auth state from localStorage
   */
  static initialize(): void {
    const storedToken = localStorage.getItem('adminAuthToken')
    const storedEmail = localStorage.getItem('adminEmail')
    
    if (storedToken && storedEmail) {
      this.authToken = storedToken
      this.adminEmail = storedEmail
    }
  }
}

// Initialize on module load
AuthService.initialize()